import React, { createContext, useContext, useState, useEffect } from 'react';
import { ModuleItem } from '../types/index.ts';

interface ModuleContextType {
  modulesList: ModuleItem[];
  loading: boolean;
  error: string | null;
  toggleModule: (code: string, isEnabled: boolean) => Promise<{ success: boolean; message?: string }>;
  isModuleActive: (code: string) => boolean;
  refreshModules: () => Promise<void>;
}

const ModuleContext = createContext<ModuleContextType | undefined>(undefined);

const DEFAULT_MODULES: ModuleItem[] = [
  { id: 1, code: 'dashboard', name: 'Dashboard', category: 'operations', icon: 'LayoutDashboard', description: 'Executive and site overview with live metrics and KPIs', isCore: true, isEnabled: true },
  { id: 2, code: 'ubl_customers', name: 'UBL Branches', category: 'operations', icon: 'Building2', description: 'UBL client hierarchy, regional hubs, and branch registry', isCore: true, isEnabled: true },
  { id: 3, code: 'tickets', name: 'Tickets', category: 'operations', icon: 'Ticket', description: 'Maintenance and renovation work order requests from branches', isCore: true, isEnabled: true },
  { id: 4, code: 'projects', name: 'Projects', category: 'operations', icon: 'FolderKanban', description: 'Branch renovation, retrofit, and maintenance projects', isCore: true, isEnabled: true },
  { id: 5, code: 'site_survey', name: 'Site Survey', category: 'operations', icon: 'ClipboardCheck', description: 'Mobile-friendly on-site measurement, survey and inspection forms', isCore: false, isEnabled: true },
  { id: 6, code: 'boq', name: 'BOQ & Estimation', category: 'operations', icon: 'Calculator', description: 'Detailed bill of quantities with materials, labour rates and revisions', isCore: false, isEnabled: true },
  { id: 7, code: 'quotations', name: 'Quotations', category: 'operations', icon: 'FileText', description: 'Formal client quotations generated automatically from approved BOQs', isCore: false, isEnabled: true },
  { id: 8, code: 'approvals', name: 'Approval Engine', category: 'system', icon: 'CheckCircle2', description: 'Multi-tiered approval workflows', isCore: true, isEnabled: true },
  { id: 9, code: 'purchase', name: 'Purchase & PO', category: 'procurement', icon: 'ShoppingCart', description: 'Procurement requests and purchase orders', isCore: false, isEnabled: true },
  { id: 10, code: 'vendors', name: 'Vendors', category: 'procurement', icon: 'Truck', description: 'Building material suppliers and contractors', isCore: true, isEnabled: true },
  { id: 11, code: 'inventory', name: 'Inventory & Materials', category: 'procurement', icon: 'Boxes', description: 'Material issues and warehouse stock', isCore: false, isEnabled: true },
  { id: 12, code: 'site_work', name: 'Site Work & Progress', category: 'operations', icon: 'Hammer', description: 'Milestone tracking and site logs', isCore: false, isEnabled: true },
  { id: 13, code: 'completion_certificates', name: 'Completion Certificates', category: 'operations', icon: 'Award', description: 'Official completion sign-off certificates', isCore: false, isEnabled: true },
  { id: 14, code: 'invoices', name: 'Invoices', category: 'finance', icon: 'Receipt', description: 'Branch-wise individual billing', isCore: false, isEnabled: true },
  { id: 15, code: 'consolidated_invoices', name: 'Consolidated Invoices', category: 'finance', icon: 'Layers', description: 'Group multiple branch invoices for UBL regional centralized billing', isCore: false, isEnabled: true, dependencies: ['invoices', 'accounts'] },
  { id: 16, code: 'accounts', name: 'Accounts & Ledger', category: 'finance', icon: 'BookOpen', description: 'Chart of accounts and financial ledger', isCore: true, isEnabled: true },
  { id: 17, code: 'payments', name: 'Payments', category: 'finance', icon: 'CreditCard', description: 'Customer receipts and vendor payouts', isCore: false, isEnabled: true },
  { id: 18, code: 'expenses', name: 'Expenses & Costing', category: 'finance', icon: 'Wallet', description: 'Site fuel, labour, and petty cash expenses', isCore: false, isEnabled: true },
  { id: 19, code: 'project_costing', name: 'Project Costing', category: 'finance', icon: 'BarChart3', description: 'Budget vs. actual expenditure', isCore: false, isEnabled: true },
  { id: 20, code: 'documents', name: 'Document Management', category: 'system', icon: 'FolderArchive', description: 'Project drawings, approval copies, and receipts', isCore: true, isEnabled: true },
  { id: 21, code: 'reports', name: 'Reports & Analytics', category: 'system', icon: 'TrendingUp', description: 'Branch-wise performance and financial summaries', isCore: true, isEnabled: true },
  { id: 22, code: 'audit_logs', name: 'Audit Trail', category: 'system', icon: 'History', description: 'Immutable log of status updates and approvals', isCore: true, isEnabled: true },
  { id: 23, code: 'module_manager', name: 'Module Manager', category: 'system', icon: 'SlidersHorizontal', description: 'ERP module activation and dependency controls', isCore: true, isEnabled: true },
  // HERE4U Operational Modules
  { id: 24, code: 'here4u_center', name: 'HERE4U Command Center', category: 'operations', icon: 'ActivitySquare', description: 'Operational command center with real-time pipeline funnel and dispatch metrics', isCore: true, isEnabled: true },
  { id: 25, code: 'gmail_center', name: 'Gmail Center & Parser', category: 'operations', icon: 'Mail', description: 'Automated monitoring of UBL email complaints, regex ticket and branch extraction', isCore: true, isEnabled: true },
  { id: 26, code: 'temporary_cases', name: 'Temporary Cases', category: 'operations', icon: 'Clock', description: 'Pre-ticket inquiry staging and duplicate-free ticket matching', isCore: false, isEnabled: true },
  { id: 27, code: 'work_orders', name: 'Work Orders', category: 'operations', icon: 'Hammer', description: 'Field mobilization execution orders with staff allocation and scope', isCore: false, isEnabled: true },
  { id: 28, code: 'staff_teams', name: 'Staff & Field Teams', category: 'people', icon: 'Users', description: 'Field engineers, electricians, masons, and specialized response units', isCore: true, isEnabled: true },
  { id: 29, code: 'field_attendance', name: 'GPS Attendance', category: 'operations', icon: 'MapPin', description: 'Geo-fenced mobile attendance with 150m branch distance audit', isCore: false, isEnabled: true },
  { id: 30, code: 'job_verification', name: 'Job Verification & Signoff', category: 'operations', icon: 'CheckCheck', description: 'Joint QA audit checklist, defect punch lists, and ticket closure', isCore: false, isEnabled: true },
  { id: 31, code: 'work_evidence', name: 'Work Evidence & Photos', category: 'operations', icon: 'Camera', description: 'Geotagged site photos categorized by Before, During, and After repair stages', isCore: false, isEnabled: true },
  { id: 32, code: 'estimator_templates', name: 'Estimator Template Engine', category: 'operations', icon: 'Calculator', description: 'Dynamic cost estimation for 9 HERE4U work types with wastage and markup', isCore: true, isEnabled: true },
];

export const ModuleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modulesList, setModulesList] = useState<ModuleItem[]>(DEFAULT_MODULES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/modules');
      if (!res.ok) {
        // Non-blocking fallback to default modules
        return;
      }
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        setModulesList(data);
      }
    } catch (err: any) {
      console.warn('Module load notice (using standard defaults):', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  const toggleModule = async (code: string, isEnabled: boolean): Promise<{ success: boolean; message?: string }> => {
    try {
      const targetMod = modulesList.find((m) => m.code === code);
      if (!targetMod) return { success: false, message: 'Module not found' };

      if (targetMod.isCore && !isEnabled) {
        return { success: false, message: 'Core module cannot be disabled.' };
      }

      // Check if disabling and others depend on it
      if (!isEnabled) {
        const dependentModules = modulesList.filter(
          (m) => m.isEnabled && Array.isArray(m.dependencies) && m.dependencies.includes(code)
        );
        if (dependentModules.length > 0) {
          const names = dependentModules.map((m) => m.name).join(', ');
          return {
            success: false,
            message: `Dependency Block: "${targetMod.name}" is required by active module(s): [${names}]. You must disable them first before disabling this module.`,
          };
        }
      }

      const res = await fetch('/api/modules/toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, isEnabled }),
      });

      const data = await res.json();
      if (!res.ok) {
        return { success: false, message: data.error || 'Failed to update module' };
      }

      setModulesList((prev) =>
        prev.map((m) => (m.code === code ? { ...m, isEnabled } : m))
      );

      return {
        success: true,
        message: `Module "${targetMod.name}" has been ${isEnabled ? 'enabled' : 'disabled'}.`,
      };
    } catch (err: any) {
      return { success: false, message: err.message || 'Network error' };
    }
  };

  const isModuleActive = (code: string): boolean => {
    const mod = modulesList.find((m) => m.code === code);
    if (!mod) {
      // Core and standard operations modules default to enabled
      return !['crm', 'hr', 'attendance', 'payroll', 'assets', 'fleet'].includes(code);
    }
    return mod.isEnabled;
  };

  return (
    <ModuleContext.Provider
      value={{
        modulesList,
        loading,
        error,
        toggleModule,
        isModuleActive,
        refreshModules: fetchModules,
      }}
    >
      {children}
    </ModuleContext.Provider>
  );
};

export const useModules = () => {
  const context = useContext(ModuleContext);
  if (!context) throw new Error('useModules must be used within ModuleProvider');
  return context;
};
